import { useState, useCallback, useEffect } from 'react';
import { IconChevronLeft, IconChevronRight, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import { SlideContent } from './services/mockSlideDeckService';
import { SlideRenderer } from './blocks/slidedeck/SlideRenderer';

interface SlideDeckTabViewProps {
  slides: SlideContent[];
  title: string;
  initialSlideIndex?: number;
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export const SlideDeckTabView = ({ 
  slides, 
  title,
  initialSlideIndex = 0 
}: SlideDeckTabViewProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);
  const [zoomLevel, setZoomLevel] = useState(1); // 100%
  
  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  }, []);
  
  const handleNext = useCallback(() => {
    setCurrentIndex(prev => Math.min(slides.length - 1, prev + 1));
  }, [slides.length]);
  
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const currentIdx = ZOOM_LEVELS.indexOf(prev);
      if (currentIdx < ZOOM_LEVELS.length - 1) {
        return ZOOM_LEVELS[currentIdx + 1];
      }
      return prev;
    });
  }, []);
  
  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const currentIdx = ZOOM_LEVELS.indexOf(prev);
      if (currentIdx > 0) {
        return ZOOM_LEVELS[currentIdx - 1];
      }
      return prev;
    });
  }, []);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        handlePrev();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrev, handleNext]);
  
  const currentSlide = slides[currentIndex];
  
  if (!currentSlide) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400">
        No slides available
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col bg-slate-100">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <h1 className="text-lg font-semibold text-slate-800 truncate">{title}</h1>
          <div className="flex items-center gap-4">
            {/* Progress dots */}
            <div className="flex items-center gap-1">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i === currentIndex
                      ? 'bg-amber-500 w-4'
                      : i < currentIndex
                        ? 'bg-amber-300'
                        : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-slate-500">
              {currentIndex + 1} / {slides.length}
            </span>
            
            {/* Zoom controls */}
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel === ZOOM_LEVELS[0]}
                className={`p-1.5 rounded transition-colors ${
                  zoomLevel === ZOOM_LEVELS[0]
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="Zoom out"
              >
                <IconZoomOut className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-500 min-w-[3rem] text-center">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                className={`p-1.5 rounded transition-colors ${
                  zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
                    ? 'text-slate-300 cursor-not-allowed'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                title="Zoom in"
              >
                <IconZoomIn className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Slide content */}
      <div className="flex-1 flex items-center justify-center p-6 min-h-0 overflow-auto">
        <div 
          className="w-full max-w-4xl aspect-[16/10] bg-white rounded-xl shadow-lg overflow-hidden transition-transform duration-200"
          style={{ transform: `scale(${zoomLevel})` }}
        >
          <SlideRenderer slide={currentSlide} />
        </div>
      </div>
      
      {/* Navigation footer */}
      <div className="flex-shrink-0 px-6 py-4 bg-white border-t border-slate-200">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              currentIndex === 0
                ? 'text-slate-300'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <IconChevronLeft className="w-4 h-4" />
            Previous
          </button>
          
          <div className="text-center">
            <p className="text-sm font-medium text-slate-700">{currentSlide.title}</p>
            <p className="text-xs text-slate-400">
              Use arrow keys or click to navigate
            </p>
          </div>
          
          <button
            onClick={handleNext}
            disabled={currentIndex === slides.length - 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              currentIndex === slides.length - 1
                ? 'text-slate-300'
                : 'bg-amber-500 text-white hover:bg-amber-600'
            }`}
          >
            Next
            <IconChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

